/**
 * Toaster Component (T050)
 *
 * Renders toast notifications using @yak/ui Toast components.
 * This component should be placed at the root of your application
 * inside a ToastProvider to display all toast notifications.
 */

'use client';

import {
  ToastOrganism,
  ToastClose,
  ToastOrganismDescription,
  ToastTitle,
  ToastViewport,
  type ToastOrganismVariant,
} from '@yak/ui';

import { useToast } from '~/hooks/useToast';

/**
 * Map internal toast variants to @yak/ui toast variants
 */
function mapVariant(
  variant: 'default' | 'destructive' | 'success' | 'warning'
): ToastOrganismVariant {
  switch (variant) {
    case 'destructive':
      return 'error';
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    default:
      return undefined; // default variant
  }
}

/**
 * Toaster component that renders all active toast notifications.
 *
 * @example
 * // In your root layout:
 * import { ToastProvider } from '@yak/ui';
 * import { Toaster } from '~/components/Toaster';
 *
 * function RootLayout({ children }) {
 *   return (
 *     <ToastProvider>
 *       {children}
 *       <Toaster />
 *     </ToastProvider>
 *   );
 * }
 */
export function Toaster() {
  const { toasts, dismiss } = useToast();

  return (
    <>
      {toasts.map((toast) => (
        <ToastOrganism
          key={toast.id}
          open={true}
          onOpenChange={(open: boolean) => {
            if (!open) {
              dismiss(toast.id);
            }
          }}
          variant={mapVariant(toast.variant)}
          duration={toast.duration}
        >
          <div className="grid gap-1">
            <ToastTitle>{toast.title}</ToastTitle>
            {toast.description && (
              <ToastOrganismDescription>{toast.description}</ToastOrganismDescription>
            )}
          </div>
          {toast.action && (
            <button
              type="button"
              className="inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium ring-offset-background transition-colors hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none disabled:opacity-50"
              onClick={toast.action.onClick}
            >
              {toast.action.label}
            </button>
          )}
          <ToastClose />
        </ToastOrganism>
      ))}
      <ToastViewport />
    </>
  );
}
